import * as ImagePickerModule from 'expo-image-picker';
import * as React from 'react';
import { Platform, Image, View, StyleSheet, Dimensions } from 'react-native';
import FontAwesome from 'react-native-vector-icons/FontAwesome';
import { translate } from 'secullum-i18n';
import {
  Card,
  FilePicker,
  getTheme,
  isTablet,
  Message,
  Modal
} from 'secullum-react-native-ui';
import { estiloBotaoDestaque } from '../../components/BotaoDestaque';
import { ButtonBar } from '../../components/ButtonBar';
import CapturarImagem from '../../components/CapturarImagem';
import { manipulateImageAsync } from '../../modules/image';
import { isDesktop } from '../../modules/layout';

interface Props {
  onConcluir: (foto: string) => void;
  onCancelar: () => void;
}

interface State {
  passo: null | 'captura' | 'escolha' | 'resultado';
  foto: string | null;
  temPermissaoGaleria: boolean;
  erroPermissaoGaleria: string;
}

class ModalCapturarFotoFuncionario extends React.Component<Props, State> {
  state: State = {
    passo: null,
    foto: null,
    temPermissaoGaleria: false,
    erroPermissaoGaleria: ''
  };

  async componentDidMount() {
    const permissaoGaleria =
      await ImagePickerModule.getMediaLibraryPermissionsAsync();

    this.setState({
      passo: 'escolha',
      temPermissaoGaleria: permissaoGaleria.granted
    });
  }

  onPressGaleriaAsync = async () => {
    if (!this.state.temPermissaoGaleria) {
      const permissaoGaleria =
        await ImagePickerModule.requestMediaLibraryPermissionsAsync();

      if (!permissaoGaleria.granted) {
        this.setState({
          erroPermissaoGaleria: translate('Sem acesso à galeria')
        });

        return;
      }
    }

    const options: ImagePickerModule.ImagePickerOptions = {
      quality: 1,
      allowsEditing: true,
      base64: false,
      exif: false
    };

    const imageResult = await ImagePickerModule.launchImageLibraryAsync(
      options
    );

    if (imageResult.canceled) {
      this.props.onCancelar();
    } else {
      const arquivo = await manipulateImageAsync(
        imageResult.assets[0].uri,
        650
      );

      this.setState({ passo: 'resultado', foto: arquivo });
    }
  };

  render() {
    switch (this.state.passo) {
      case 'escolha':
        return this.renderEscolha();
      case 'captura':
        return this.renderCaptura();
      case 'resultado':
        return this.renderResultado();
      default:
        return null;
    }
  }

  renderEscolha() {
    const { erroPermissaoGaleria } = this.state;
    const { onCancelar } = this.props;

    return (
      <Modal
        visible
        onRequestClose={Platform.OS === 'web' ? undefined : onCancelar}
        overlayStyle={styles.modal}
      >
        <Card
          style={[
            styles.modalContainer,
            {
              width: isTablet() ? 500 : isDesktop() ? 650 : '90%'
            }
          ]}
          nativeID="modal-escolha"
        >
          <View style={styles.modalHeader}>
            <Card.Header title={translate('Selecione a origem')} />
            {Platform.OS == 'web' && (
              <FontAwesome
                name="times"
                color={theme.textColor1}
                size={20}
                onPress={onCancelar}
                nativeID="modal-escolha-fechar"
                style={styles.modalBotaoFecharWeb}
              />
            )}
          </View>
          <Card.Section>
            <View style={styles.pickersContainer}>
              <View style={[styles.pickerContainer, { paddingRight: 8 }]}>
                <FilePicker
                  label={translate('Câmera')}
                  icon={'camera'}
                  onPress={() => this.setState({ passo: 'captura' })}
                  nativeID="modal-escolha-camera"
                />
              </View>
              <View style={[styles.pickerContainer, { paddingLeft: 8 }]}>
                <FilePicker
                  label={translate('Galeria')}
                  icon={'photo'}
                  onPress={this.onPressGaleriaAsync}
                  nativeID="modal-escolha-galeria"
                />
              </View>
            </View>
          </Card.Section>
        </Card>
        {/* Sec-issues#12245: Só precisamos tratar a mensagem de erro de acesso a galeria,
        pois a mensagem de erro de acesso a camera é tratada pelo componente CapturarImagem. */}
        <Message
          type="warning"
          message={erroPermissaoGaleria}
          visible={erroPermissaoGaleria !== ''}
          onRequestClose={() => {
            this.setState({ erroPermissaoGaleria: '' });
            onCancelar();
          }}
          nativeID="modal-permissao-galeria-mensagem-erro"
        />
      </Modal>
    );
  }

  renderCaptura() {
    const { onCancelar } = this.props;

    return (
      <Modal
        visible
        onRequestClose={Platform.OS === 'web' ? undefined : onCancelar}
        overlayStyle={styles.modal}
      >
        <View style={styles.modalCapturarFoto}>
          <CapturarImagem
            onConcluir={foto => this.setState({ foto, passo: 'resultado' })}
            onFechar={onCancelar}
            larguraImagem={642}
            cameraPadrao={'front'}
            permiteAlterarCamera={true}
            mensagemErroCameraNaoEncontrada={translate(
              'Não foi possível encontrar uma WebCam para realizar a captura.'
            )}
          />
        </View>
      </Modal>
    );
  }

  renderResultado() {
    const { foto } = this.state;
    const { onConcluir, onCancelar } = this.props;

    const mediumScreen = Dimensions.get('window').width <= 800;
    const smallScreen = Dimensions.get('window').width <= 500;

    return (
      <Modal visible overlayStyle={styles.modal}>
        <View
          style={[
            styles.modalContainer,
            Platform.OS === 'web'
              ? [
                  styles.modalCapturarFotoWeb,
                  {
                    height: smallScreen ? 300 : mediumScreen ? 427 : 590
                  }
                ]
              : styles.modalCapturarFoto
          ]}
        >
          <Image
            source={{
              uri:
                foto?.indexOf('data:image') == -1
                  ? `data:image/jpeg;base64,${foto}`
                  : foto?.toString()
            }}
            style={
              Platform.OS === 'web'
                ? [
                    styles.fotoWeb,
                    {
                      width: smallScreen ? 262 : mediumScreen ? 422 : 642,
                      height: smallScreen ? 196 : mediumScreen ? 320 : 482
                    }
                  ]
                : [styles.foto]
            }
          />
          <ButtonBar
            buttonBarStyle={styles.espaco}
            leftButton={{
              text: translate('Cancelar'),
              onPress: onCancelar,
              primary: false,
              nativeID: 'capturar-imagem-cancelar'
            }}
            rightButton={{
              text: translate('Continuar'),
              onPress: () => onConcluir(foto!),
              nativeID: 'capturar-imagem-continuar',
              style: estiloBotaoDestaque
            }}
          />
        </View>
      </Modal>
    );
  }
}

const theme = getTheme();

const styles = StyleSheet.create({
  modal: {
    flex: 1,
    justifyContent: 'center'
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  modalContainer: {
    backgroundColor: theme.backgroundColor1,
    margin: 0,
    borderRadius: 5,
    alignSelf: 'center'
  },
  modalExigirFoto: {
    margin: 16,
    paddingTop: 10,
    maxWidth: Platform.OS === 'web' ? 400 : undefined,
    alignSelf: Platform.OS === 'web' ? 'center' : undefined
  },
  modalCapturarFoto: {
    height: '100%',
    width: '100%'
  },
  modalCapturarFotoWeb: {
    alignSelf: 'center'
  },
  modalBotaoFecharWeb: {
    alignSelf: 'center',
    paddingRight: 16
  },
  foto: {
    margin: 16,
    marginBottom: 0,
    flex: 1
  },
  fotoWeb: {
    alignSelf: 'center',
    marginLeft: 20,
    marginRight: 20,
    marginTop: 30,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#0000003b'
  },
  espaco: {
    padding: 16
  },
  espacoCheckbox: {
    paddingLeft: 16
  },
  textoCheckbox: {
    fontSize: 14
  },
  buttonCancelar: {
    margin: 16
  },
  pickersContainer: {
    flexDirection: 'row'
  },
  pickerContainer: {
    flex: 1
  }
});

export default ModalCapturarFotoFuncionario;
